import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatchApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetHomeMetricsConfiguration,
  startGetTotalStatisticsAllCampaigns,
  startSetHomeMetricsConfiguration,
} from "../../actions/adsCampaigns";
import StatisticCard from "./StatisticCard";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  OVERVIEW_HOME_STATISTICS,
  OVERVIEW_HOME_STATISTICS_VIEW_MORE,
  STATISTICS_ONLY_BUZZLY_CAMPAIGNS,
  PREVIOUS_PERIOD,
} from "../../i18n/keysTranslations";
import { Link } from "react-router-dom";
import {
  buildDefaultLoadingStatisticsCards,
  buildStatisticsCardsFromTotals,
} from "./buildStatisticsCardsFromTotals";
import resolveHomeKpiBreakdown from "./resolveHomeKpiBreakdown";
import { useMediaQuery, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import Button from "../Buttons/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MeasurableKpiSelector from "../../features/measurable-kpi/components/MeasurableKpiSelector";
import normalizeHomeMetricsPrimaryKpi from "../../features/measurable-kpi/utils/normalizeHomeMetricsPrimaryKpi";

const CampaignStatisticsOverview = ({
  dateRange = null,
  isLoading = false,
}) => {
  const { t } = useTranslationApp();
  const { control, watch, setValue } = useForm({
    defaultValues: {
      measurableKpi: null,
    },
  });
  const measurableKpi = watch("measurableKpi");
  const hasLoadedPrimaryKpiRef = useRef(false);
  const lastSyncedPrimaryKpiRef = useRef(undefined);

  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const [statisticsData, setStatisticsData] = useState(null);
  const [loadingStatistics, setLoadingStatistics] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const hasExplicitDateRange = !!(
    dateRange &&
    dateRange.startDateTime &&
    dateRange.endDateTime
  );

  const hasDateContext = dateRange !== null && dateRange !== undefined;
  const shouldFetchStatistics = hasDateContext;

  const totals = statisticsData?.totals || {};
  const previousTotals = statisticsData?.totalsLastPeriod || {};
  const showPreviousPeriod =
    hasExplicitDateRange && !!statisticsData?.totalsLastPeriod;

  const statisticsCards = buildStatisticsCardsFromTotals({
    totals,
    previousTotals,
    showPreviousPeriod,
    t,
  });

  const defaultLoadingCards = buildDefaultLoadingStatisticsCards(t);

  const cardsToRender =
    statisticsCards.length > 0 ? statisticsCards : defaultLoadingCards;

  const isLoadingData =
    isLoading || loadingStatistics || statisticsCards.length === 0;

  const overviewRequestEpochRef = useRef(0);

  const fetchStatistics = useCallback(async () => {
    if (!shouldFetchStatistics) {
      return;
    }

    const requestEpoch = ++overviewRequestEpochRef.current;
    const startP = dateRange?.startDateTime || null;
    const endP = dateRange?.endDateTime || null;

    setLoadingStatistics(true);
    try {
      const params = {
        startDateTime: startP,
        endDateTime: endP,
      };

      if (measurableKpi) {
        params.measurableKpi = measurableKpi;
      }

      if (startP && endP) {
        const breakdown = resolveHomeKpiBreakdown({
          startDateTime: startP,
          endDateTime: endP,
        });
        if (breakdown) {
          params.breakdown = breakdown;
        }
      }

      const response = await dispatch(
        startGetTotalStatisticsAllCampaigns(params)
      );

      if (requestEpoch !== overviewRequestEpochRef.current) {
        return;
      }

      if (response?.ok && response?.data) {
        setStatisticsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching campaign statistics:", error);
    } finally {
      if (requestEpoch === overviewRequestEpochRef.current) {
        setLoadingStatistics(false);
      }
    }
  }, [
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    measurableKpi,
    shouldFetchStatistics,
  ]);

  const scrollLeft = () => {
    if (isMobile) {
      setCurrentCardIndex((prev) => Math.max(0, prev - 1));
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (isMobile) {
      setCurrentCardIndex((prev) =>
        Math.min(cardsToRender.length - 1, prev + 1)
      );
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const calculateCardsDontFit = () => {
    const cardWidth = 280;
    const gap = 16;
    const totalCardsWidth =
      cardsToRender.length * cardWidth + (cardsToRender.length - 1) * gap;
    const containerWidth = windowWidth - 252;
    return totalCardsWidth > containerWidth;
  };

  const shouldShowScrollButtons = (() => {
    if (isMobile) {
      return statisticsCards.length > 1;
    }

    if (isTablet || isDesktop) {
      return calculateCardsDontFit();
    }

    return false;
  })();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentCardIndex(0);
  }, [measurableKpi]);

  useEffect(() => {
    const loadSavedPrimaryKpi = async () => {
      const response = await dispatch(startGetHomeMetricsConfiguration());
      let normalizedPrimaryKpi = null;

      if (response?.ok) {
        normalizedPrimaryKpi = normalizeHomeMetricsPrimaryKpi(
          response?.data?.primaryKpi
        );
      }

      lastSyncedPrimaryKpiRef.current = normalizedPrimaryKpi;
      setValue("measurableKpi", normalizedPrimaryKpi, {
        shouldDirty: false,
        shouldTouch: false,
      });
      hasLoadedPrimaryKpiRef.current = true;
    };

    loadSavedPrimaryKpi();
  }, [dispatch, setValue]);

  useEffect(() => {
    if (!hasLoadedPrimaryKpiRef.current) {
      return;
    }

    if (lastSyncedPrimaryKpiRef.current === measurableKpi) {
      return;
    }

    lastSyncedPrimaryKpiRef.current = measurableKpi;

    dispatch(
      startSetHomeMetricsConfiguration({
        metricsConfiguration: {
          primaryKpi: measurableKpi || null,
        },
      })
    );
  }, [dispatch, measurableKpi]);

  useEffect(() => {
    if (!shouldFetchStatistics) {
      return;
    }

    fetchStatistics();
  }, [fetchStatistics, shouldFetchStatistics]);

  const renderStatisticCard = (stat, index) => (
    <StatisticCard
      key={index}
      label={stat.label}
      value={stat.value}
      previousValue={stat.previousValue}
      currency={stat.currency}
      suffix={stat.suffix}
      platformBreakdown={stat.platformBreakdown}
      hideChangeText={true}
      hideIcon
      showPreviousPeriod={showPreviousPeriod}
      previousPeriodLabel={t(PREVIOUS_PERIOD)}
      isLoading={isLoadingData}
      growth={stat.growth}
      sparklineValues={stat.sparklineValues}
      sparklineTrend={stat.sparklineTrend}
      sx={{ height: "100%", width: "100%" }}
    />
  );

  const renderMobileView = () => (
    <Box sx={{ width: "100%", py: 1 }}>
      {renderStatisticCard(statisticsCards[currentCardIndex], 0)}
    </Box>
  );

  const renderGridView = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: `repeat(${Math.min(statisticsCards.length, 5)}, 1fr)`,
        },
        gap: 2,
        width: "100%",
        py: 1,
        px: 0,
      }}
    >
      {statisticsCards.map(renderStatisticCard)}
    </Box>
  );

  const renderScrollView = () => (
    <Box
      ref={scrollContainerRef}
      sx={{
        overflowX: "auto",
        overflowY: "visible",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollBehavior: "smooth",
        py: 1,
        px: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          minWidth: "max-content",
          scrollSnapType: "x mandatory",
          alignItems: "stretch",
        }}
      >
        {statisticsCards.map((stat, index) => (
          <Box
            key={index}
            sx={{
              width: 280,
              flexShrink: 0,
              scrollSnapAlign: "start",
              display: "flex",
            }}
          >
            {renderStatisticCard(stat, index)}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 0 },
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t(OVERVIEW_HOME_STATISTICS)}
          </Typography>
          <Tooltip
            title={t(STATISTICS_ONLY_BUZZLY_CAMPAIGNS, { NAME_BUSINESS })}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "12px",
                },
              },
            }}
          >
            <InfoIcon
              sx={{
                fontSize: 18,
                color: "text.secondary",
                cursor: "help",
              }}
            />
          </Tooltip>
          <Typography
            component={Link}
            to="/report"
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              textDecoration: "underline",
              textUnderlineOffset: "2px",
              width: "fit-content",
              "&:hover": {
                color: "text.primary",
              },
            }}
          >
            {t(OVERVIEW_HOME_STATISTICS_VIEW_MORE)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          <MeasurableKpiSelector control={control} disabled={isLoading} />
          {shouldShowScrollButtons ? (
            <>
              <Button
                color="secondary"
                size="small"
                onClick={scrollLeft}
                sx={{
                  minWidth: 32,
                  height: 32,
                  p: 0,
                  borderColor: "grey.300",
                  "&:hover": {
                    borderColor: "grey.400",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </Button>
              <Button
                color="secondary"
                size="small"
                onClick={scrollRight}
                sx={{
                  minWidth: 32,
                  height: 32,
                  p: 0,
                  borderColor: "grey.300",
                  "&:hover": {
                    borderColor: "grey.400",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ position: "relative" }}>
        {isMobile
          ? renderMobileView()
          : (isTablet || isDesktop) && !shouldShowScrollButtons
            ? renderGridView()
            : renderScrollView()}
      </Box>
    </Box>
  );
};

export default CampaignStatisticsOverview;
